import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { repositoryAPI, userAPI } from '../services/api'
import CertifiedBadge from './CertifiedBadge'
import Modal from './Modal'
import './ManageRepositoryModal.css'

const STATS = [
  { key: 'forks', label: 'Forks' },
  { key: 'releases', label: 'Releases' },
  { key: 'bookmarks', label: 'Bookmarks' },
  { key: 'commits', label: 'Commits' },
  { key: 'branches', label: 'Branches' },
  { key: 'open_pull_requests', label: 'Open pull requests' },
  { key: 'open_issues', label: 'Open issues' },
]

function ManageRepositoryModal({ repo, currentUserId, onClose, onDeleted, onSelfRemoved }) {
  const navigate = useNavigate()
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [usernameQuery, setUsernameQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [actionError, setActionError] = useState('')
  const [addingId, setAddingId] = useState(null)
  const [removingId, setRemovingId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const addStarted = useRef(false)

  useEffect(() => {
    if (!repo?.slug) {
      setOverview(null)
      setUsernameQuery('')
      setResults([])
      setError('')
      setActionError('')
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError('')
    repositoryAPI.getManage(repo.slug)
      .then((data) => {
        if (!cancelled) setOverview(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load repository')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [repo?.slug])

  useEffect(() => {
    if (!repo?.slug) return undefined
    const q = usernameQuery.trim()
    if (q.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(() => {
      if (!cancelled) setSearching(true)
      userAPI.search(q, repo.slug)
        .then((data) => {
          if (!cancelled) setResults(data.users || [])
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [usernameQuery, repo?.slug])

  const ownerIds = new Set((overview?.owners || []).map((owner) => owner.id))

  const handleAdd = (user) => {
    if (addStarted.current || !repo?.slug || !user?.username) return
    addStarted.current = true
    const username = user.username
    const userId = user.id
    // Open the confirm after this click finishes. Calling it during the press
    // lets the same click dismiss the dialog, so the first press appears to do nothing.
    window.setTimeout(() => {
      const confirmed = window.confirm(
        `Add ${username} as an owner?\n\nThey will have full owner access, including deleting this repository.`,
      )
      if (!confirmed) {
        addStarted.current = false
        return
      }
      setAddingId(userId)
      setActionError('')
      repositoryAPI.addOwner(repo.slug, username)
        .then((data) => {
          setOverview(data)
          setUsernameQuery('')
          setResults([])
        })
        .catch((err) => {
          setActionError(err.message || 'Failed to add owner')
        })
        .finally(() => {
          addStarted.current = false
          setAddingId(null)
        })
    }, 0)
  }

  const handleRemove = async (user) => {
    if (!repo?.slug || !user?.id) return
    if (!window.confirm(`Remove ${user.username} as an owner?`)) return
    setRemovingId(user.id)
    setActionError('')
    try {
      const data = await repositoryAPI.removeOwner(repo.slug, user.id)
      if (currentUserId && String(user.id) === String(currentUserId)) {
        if (onSelfRemoved) onSelfRemoved(repo.slug)
        else onClose()
        return
      }
      setOverview(data)
    } catch (err) {
      setActionError(err.message || 'Failed to remove owner')
    } finally {
      setRemovingId(null)
    }
  }

  const handleDelete = async () => {
    if (!repo?.slug) return
    const label = repo.title || repo.slug
    const confirmed = window.confirm(
      `Delete "${label}"?\n\nIt will be removed from browse and your dashboard. This uses the same hide as admin suspend (not a permanent wipe). Contact support if you need it restored.`,
    )
    if (!confirmed) return
    setDeleting(true)
    setActionError('')
    try {
      await repositoryAPI.deleteOwn(repo.slug)
      onDeleted(repo.slug)
    } catch (err) {
      setActionError(err.message || 'Failed to delete repository')
      setDeleting(false)
    }
  }

  return (
    <Modal
      open={Boolean(repo)}
      onClose={onClose}
      title={repo?.title || 'Manage repository'}
      maxWidth="720px"
      ariaLabelledBy="manage-repo-title"
    >
      <div className="manage-repo">
        {repo?.slug && <p className="manage-repo-slug">{repo.slug}</p>}
        {loading && <p className="manage-repo-muted">Loading...</p>}
        {error && <p className="manage-repo-error">{error}</p>}
        {!loading && !error && overview && (
          <>
            <section className="manage-repo-section">
              <h3>Stats</h3>
              <div className="manage-repo-stats">
                {STATS.map((stat) => (
                  <div key={stat.key} className="manage-repo-stat">
                    <span className="manage-repo-stat-value">{overview.stats?.[stat.key] ?? 0}</span>
                    <span className="manage-repo-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="manage-repo-section">
              <h3>Actions</h3>
              <div className="manage-repo-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/repositories/${repo.slug}`)}
                >
                  View repository
                </button>
                <button
                  type="button"
                  className="btn btn-secondary repos-you-manage-delete-btn"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </section>

            <section className="manage-repo-section">
              <h3>Owners</h3>
              <p className="manage-repo-warning" role="note">
                Adding a user gives them full owner access, including deleting this repository. They can also approve pull requests, publish releases, and roll back.
              </p>
              <ul className="manage-repo-owners">
                {(overview.owners || []).map((owner) => (
                  <li key={owner.id} className="manage-repo-owner">
                    <span className="manage-repo-owner-name">
                      {owner.username}
                      <CertifiedBadge user={owner} />
                      <span className="manage-repo-owner-role">{owner.is_primary ? 'Creator' : 'Owner'}</span>
                    </span>
                    {!owner.is_primary && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleRemove(owner)}
                        disabled={removingId === owner.id}
                      >
                        {removingId === owner.id ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              <label className="manage-repo-search-label" htmlFor="manage-repo-owner-search">
                Add an owner by username
              </label>
              <input
                id="manage-repo-owner-search"
                className="form-input manage-repo-search"
                type="text"
                value={usernameQuery}
                onChange={(e) => setUsernameQuery(e.target.value)}
                placeholder="Search username"
                autoComplete="off"
              />
              {searching && results.length === 0 && <p className="manage-repo-muted">Searching...</p>}
              {!searching && usernameQuery.trim().length >= 2 && results.length === 0 && (
                <p className="manage-repo-muted">No matching users.</p>
              )}
              {results.length > 0 && (
                <ul className="manage-repo-results">
                  {results.map((user) => {
                    const already = ownerIds.has(user.id)
                    return (
                      <li key={user.id} className="manage-repo-owner">
                        <span className="manage-repo-owner-name">
                          {user.username}
                          <CertifiedBadge user={user} />
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleAdd(user)}
                          disabled={already || addingId === user.id}
                        >
                          {already ? 'Already an owner' : addingId === user.id ? 'Adding…' : 'Add'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
              {actionError && <p className="manage-repo-error">{actionError}</p>}
            </section>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ManageRepositoryModal
